import {
  validationKeys,
  typeOptionsValueToOptionMap,
  featuresOptionsValueToOptionMap,
  featuresTextOnScreenMap,
  typesTextOnScreenMap,
  trafficStage,
  addressConfirmationStages,
  directBuyNumberStages,
  featuresInitialValueMap,
  typesInitialValueMap,
} from './constants';
import { countriesMap } from '../countriesMap';
import { changeDateFormat, getTimeLeft } from '../functions';

function getPhoneNumberCountry(number) {
  const country = countriesMap[number.country_code ? number.country_code : number.country];
  if (!country) {
    console.error(`Phone number ${number.phoneNumber} is missing country data!`);
    return {name: '', code: '', callingCode: ''};
  }
  return country;
}

const getAvailableOptions = (options, accessor, country, countriesValidationsMap) => {
  if (
    !country ||
    !countriesValidationsMap ||
    !countriesValidationsMap[validationKeys.FEATURES_UNAVAILABLE] ||
    !countriesValidationsMap[validationKeys.FEATURES_UNAVAILABLE][0] ||
    countriesValidationsMap[validationKeys.FEATURES_UNAVAILABLE][0][accessor].includes('ANY')
  ) {
    return options;
  }
  return options.map(option => ({
    ...option,
    disabled: countriesValidationsMap[validationKeys.FEATURES_UNAVAILABLE][0][accessor].includes(
      option.nameInValidations
    ),
  }));
};

const isMatchingValidation = (validation, feature, type) =>
  (validation.numberFeaturesAsEnum.includes(
    feature ? featuresOptionsValueToOptionMap[feature].nameInValidations : 'ANY'
  ) ||
    validation.numberFeaturesAsEnum.includes('ANY')) &&
  (validation.numberTypesAsEnum.includes(type ? typeOptionsValueToOptionMap[type].nameInValidations : 'ANY') ||
    validation.numberTypesAsEnum.includes('ANY'));

const getMatchingValidations = (validations, feature, type) =>
  validations.filter(validation => isMatchingValidation(validation, feature, type));

const getValidationForSelection = (countryValidations = {}, feature, type) =>
  Object.keys(countryValidations).reduce((acc, current) => {
    const matchingValidations = getMatchingValidations(countryValidations[current], feature, type);
    return matchingValidations.length > 0 ? { ...acc, [current]: matchingValidations } : acc;
  }, {});

const getValidationForSelectionWithTextOnScreen = (countryValidations = {}, feature, type) =>
  getValidationForSelection(
    countryValidations,
    featuresTextOnScreenMap[feature].value,
    typesTextOnScreenMap[type].value
  );

const isRequestFormRequired = validations => validations[validationKeys.NEW_REQUEST_NUMBER];

const isAddressFormRequired = validations => validations[validationKeys.REQUEST_NUMBER];

const isAddressFormRequiredDuringNumberPurchase = validations =>
  validations[validationKeys.REGIONAL_ADDRESS] ||
  validations[validationKeys.ANY_COUNTRY] ||
  validations[validationKeys.COUNTRY_ADDRESS];

const isRequestRequired = validations =>
  (isRequestFormRequired(validations) || isAddressFormRequired(validations)) &&
  !isAddressFormRequiredDuringNumberPurchase(validations);

const isAnyCountry = validations => validations[validationKeys.ANY_COUNTRY];

const isTrafficRestricted = validations => validations[validationKeys.TRAFFIC_RESTRICTION];

const numberNeedsAddressConfirmation = (validationFeatures, validationTypes, numberFeature, numberType) => {
  const featureMap = featuresTextOnScreenMap[numberFeature];
  const typeMap = typesTextOnScreenMap[numberType];
  return (
    validationFeatures &&
    validationTypes &&
    validationFeatures.includes(featureMap ? featureMap.nameInValidations : numberFeature) &&
    validationTypes.includes(typeMap ? typeMap.nameInValidations : numberType)
  );
};

const attachTrafficModal = (stages = [], asFirstStage) => {
  stages.splice(asFirstStage ? 0 : 1, 0, trafficStage);
  return stages;
};

const getStagesForBuyModal = ({ features: feats, type }, countryValidations) => {
  const validations = getValidationForSelectionWithTextOnScreen(countryValidations, feats, type);
  const addressValidations =
    validations[validationKeys.COUNTRY_ADDRESS] ||
    validations[validationKeys.REGIONAL_ADDRESS] ||
    validations[validationKeys.ANY_COUNTRY];
  const { numberFeaturesAsEnum, numberTypesAsEnum } = addressValidations ? addressValidations[0] : {};
  const stages = numberNeedsAddressConfirmation(numberFeaturesAsEnum, numberTypesAsEnum, feats, type)
    ? addressConfirmationStages
    : directBuyNumberStages;
  return isTrafficRestricted(validations) ? attachTrafficModal([...stages], true) : [...stages];
};

// created to match the number address object in the backend
const createAddressDTO = ({
  selectedAddress,
  addressDetails: {
    town,
    addressCountry,
    streetAddress,
    additionalInfo,
    postcode,
    state,
    companyName,
    fullName,
    regionallyRestricted,
  },
  apiKey,
  number: { country, msisdn },
}) => ({
  addressCountry,
  numberCountry: country,
  msisdn,
  number: msisdn,
  accountId: apiKey,
  idFromPCA: selectedAddress,
  town,
  streetAddress,
  additionalInfo,
  postcode,
  companyName,
  fullName,
  state,
  regionallyRestricted,
});

const createRequestNumberAddressFormDTO = ({
  features,
  type,
  selectedAddress,
  addressDetails: { town, streetAddress, additionalInfo, postcode, state, companyName, fullName, addressCountry },
  apiKey,
  country,
  msisdn,
}) => ({
  addressCountry,
  numberCountry: country,
  accountId: apiKey,
  idFromPCA: selectedAddress,
  city: town,
  addressLine1: streetAddress,
  addressLine2: additionalInfo,
  postcode,
  companyName,
  fullName,
  region: state,
  features,
  type,
  msisdn,
  number: msisdn,
});

const createBuyNumberDTO = ({ apiKey, type, features, msisdn, country }) => ({
  accountId: apiKey,
  type,
  features,
  msisdn,
  country,
  number: msisdn,
  numberCountry: country,
});
// end of dto creators

const removeCallingCode = (number, countryCode) => {
  if (!number || !countryCode) return null;

  const { callingCode } = countriesMap[countryCode];
  const callingCodeLength = callingCode.length - 1; // -1 to ignore the plus
  if (callingCode === `+${number.substr(0, callingCodeLength)}`) {
    return number.slice(callingCodeLength);
  }
  return number;
};

const seperateCallingCode = (number, code) => {
  const codeLength = code.length - 1; // -1 to ignore the plus
  if (code === `+${number.substr(0, codeLength)}`) {
    return `(${code}) ${number.slice(codeLength)}`;
  }
  return number;
};

const handleGetCountryValidationsResponse = data =>
  Object.keys(data).reduce(
    (acc, currentKey) => ({
      ...acc,
      [currentKey]: data[currentKey].reduce(
        (accum, currentRule) => ({
          ...accum,
          [currentRule.validationType]: accum[currentRule.validationType]
            ? [...accum[currentRule.validationType], currentRule]
            : [currentRule],
        }),
        {}
      ),
    }),
    {}
  );

const formatNumberAndSubscription = numbersAndSubscription =>
  numbersAndSubscription.reduce(
    (accumulator, { number, subscriptionTransaction }) => ({
      ...accumulator,
      [number.msisdn]: {
        ...number,
        ...subscriptionTransaction,
        countryName: getPhoneNumberCountry(number).name,
        msisdnAndCallingCode: seperateCallingCode(number.msisdn, getPhoneNumberCountry(number).callingCode),
        type: typesInitialValueMap[number.type].label,
        features: number.features
          .reverse()
          .map(feat => featuresInitialValueMap[feat].label || feat)
          .join(' & '),
        timeLeft:
          subscriptionTransaction && subscriptionTransaction.nextBillingDate
            ? `In ${getTimeLeft(subscriptionTransaction.nextBillingDate)}`
            : '',
        formattedDate:
          subscriptionTransaction && subscriptionTransaction.nextBillingDate
            ? changeDateFormat(subscriptionTransaction.nextBillingDate)
            : '',
      },
    }),
    {}
  );

const getAmountOfActiveNumbers = (numbers) => {
  return numbers.filter(number => number.status === 'Active').length;
}

const formatLinkingNumbers = numbers =>
  numbers.map(number => ({
    ...number,
    countryName: getPhoneNumberCountry(number).name,
    msisdnAndCallingCode: seperateCallingCode(
      number.phoneNumber,
        getPhoneNumberCountry(number).callingCode
    ),
    type: number.type ? typesInitialValueMap[number.type].label : '',
    features: number.features
      ? number.features
          .reverse()
          .map(feat => featuresInitialValueMap[feat].label || feat)
          .join(' & ')
      : '',
  }));

const formatBuyNumbers = numbers =>
  numbers.reduce(
    (accumulator, number) => ({
      ...accumulator,
      [number.msisdn]: {
        ...number,
        countryName: getPhoneNumberCountry(number).name,
        msisdnAndCallingCode: seperateCallingCode(number.msisdn, getPhoneNumberCountry(number).callingCode),
        type: typesInitialValueMap[number.type].label,
        features: number.features
          .reverse()
          .map(feat => featuresInitialValueMap[feat].label || feat)
          .join(' & '),
      },
    }),
    {}
  );

const processPhoneNumbers = (phoneArray)  => {
  const statusMapping = {
    'Not Started': [
      'UNLINKED', 'WAITING_FOR_VERIFIED_CAMPAIGN', 'WAITING_FOR_NUMBER_POOLING',
      'FOLLOWER_NEED_TO_BE_LINKED', 'PROVIDER_UNKNOWN'
    ],
    'In Progress': [
      'WAITING_FOR_MIGRATION', 'MIGRATION_SUCCESS', 'MIGRATION_PENDING',
      'WAITING_FOR_UNLINK', 'NEED_TO_BE_LINKED_AGAIN', 'UNLINKING', 'LINKING'
    ],
    'Active': ['LINKED', 'APPROVED'],
    'Manual Activation Initiated': [
      'PROVIDER_CANNOT_BE_DETERMINE_RECHECK', 'PROVIDER_CANNOT_BE_DETERMINE', 'REJECTED', 'MIGRATION_FAILURE',
    ],
  };

  const statusesToRemove = ['UNASSIGNED', 'NOT_SMSABLE'];
  const usTerritories = ['AS', 'GU', 'MP', 'PR', "VI"];
  const allowedCountries = [...usTerritories, 'US', 'CA'];

  return phoneArray
      .filter(phone =>
          !statusesToRemove.includes(phone.status) &&
          allowedCountries.includes(phone.country)
      )
      .map(phone => {
        let newStatus = 'Unknown';
        let statusShownToVsu = phone.status;

        if (phone.rejectionReason === 'VONAGE_NNID_LOA' && phone.status === 'REJECTED') {
          statusShownToVsu = "VONAGE_NNID_LOA_REJECTED";
          // We show VONAGE_NNID status only to VSU
        }
        if (phone.rejectionReason === 'LOA' && phone.status === 'REJECTED') {
          newStatus = 'LOA is Required';
          statusShownToVsu = "LOA_REJECTED";
        }
        else {
          for (const [mappedStatus, statusList] of Object.entries(statusMapping)) {
            if (statusList.includes(phone.status)) {
              newStatus = mappedStatus;
              break;
            }
          }
        }

    return {
      phoneNumber: phone.phoneNumber,
      provider: phone.provider,
      vsuStatus: statusShownToVsu,
      status: newStatus,
      vsuStatusInfo: rawStatusToInfoMap[statusShownToVsu],
      lastError: phone.rejectedMessage
    };
  });
}

const rawStatusToInfoMap = {
  LINKED: "This number is linked to the campaign and able to send SMS.",
  UNLINKED: "Waiting for the campaign to be ACTIVE. It will be linked automatically afterward. If the campaign is active, press the 'Sync Numbers' button to sync it.",
  PROVIDER_CANNOT_BE_DETERMINE: "The number encountered an issue, and we couldn't determine whether it belongs to Vonage API or Inteliquent. Press the 'Sync Numbers' button for resolution.",
  PROVIDER_UNKNOWN: "The number was just added to our DB. It will undergo an internal process.",
  APPROVED: "This number is linked to the campaign and able to send SMS.",
  REJECTED: "The number had a problem in the registration process. Press the 'Sync Numbers' button.",
  WAITING_FOR_VERIFIED_CAMPAIGN: "Waiting for the campaign to be ACTIVE. It will be linked once the campaign is verified.",
  LOA_REJECTED: "Customer must provide LOA before number can be linked",
  VONAGE_NNID_LOA_REJECTED: "Unexpected LOA error - please open a BENDDEV ticket for resolution.",
  WAITING_FOR_MIGRATION: "The number is waiting to be migrated from Inteliquent TO Vonage API. Can take 1-3 days.",
  MIGRATION_FAILURE: "The number was unable to migrate to Vonage API. Please contact the support team(Numbers Team).",
  MIGRATION_SUCCESS: "The number was Migrated successfully to Vonage API.",
  MIGRATION_PENDING: "The number is waiting to be migrated from Inteliquent to Vonage API. Can take 1-3 days",
  WAITING_FOR_NUMBER_POOLING: "The account has more than 50 numbers. Number pooling process was activated and waiting to be approved. This can take a few weeks.",
  UNASSIGNED: "The number was removed from extension and is not able to send messages.",
  NOT_SMSABLE: "The number cannot send messages and will be deleted from the account in 1 day.",
  WAITING_FOR_UNLINK: "The number got some error in the registration process and will be unlinked and linked again automatically.",
  NEED_TO_BE_LINKED_AGAIN: "The number got some error in the registration process and will be linked again automatically.",
  UNLINKING: "The number got some error in the registration process and will be unlinked and linked again automatically.",
  LINKING: "The number is being verified for approval on Vonage API.",
  FOLLOWER_NEED_TO_BE_LINKED: "The number is on a child account and is waiting for the main account to be approved."
}

export {
  createBuyNumberDTO,
  createAddressDTO,
  createRequestNumberAddressFormDTO,
  isTrafficRestricted,
  isAddressFormRequired,
  isAddressFormRequiredDuringNumberPurchase,
  isAnyCountry,
  isRequestFormRequired,
  isRequestRequired,
  getStagesForBuyModal,
  getValidationForSelection,
  getMatchingValidations,
  getValidationForSelectionWithTextOnScreen,
  numberNeedsAddressConfirmation,
  handleGetCountryValidationsResponse,
  attachTrafficModal,
  getAvailableOptions,
  formatBuyNumbers,
  formatNumberAndSubscription,
  formatLinkingNumbers,
  seperateCallingCode,
  removeCallingCode,
  getAmountOfActiveNumbers,
  processPhoneNumbers
};
